/**
 * .glb / .obj 내보내기.
 *
 * glb 하나면 Unity·Unreal·Godot·Blender가 전부 읽는다. 텍스처가 파일 안에
 * 들어가므로 폴더째 옮길 필요도 없다. 게임 에셋으로 쓸 거라면 이쪽이다.
 *
 * 주의: 우리가 셰이더에 끼워 넣은 스타일(툰·림·이리데슨스)은 **나가지 않는다.**
 * glTF에 그런 칸이 없기 때문이다. 나가는 것은 지오메트리와 PBR 맵 한 벌이고,
 * 그것이 다른 엔진에서 재현 가능한 전부다. 스타일은 엔진 쪽 셰이더에서 다시 얹어야 한다.
 */

import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';

const CUSTOM_ATTRS = ['aShell', 'aSmoothN', 'aVisA', 'aVisB'];

/**
 * 스킨드 메시 + 클립을 glb 로. 셰이더 패치와 커스텀 어트리뷰트는 잠시 떼었다 되돌린다 —
 * glTF 에 그런 칸이 없고, 붙여 두면 익스포터가 경고를 쏟는다.
 */
export async function exportGLB(mesh: THREE.Mesh, name = 'texel-forge'): Promise<Blob> {
  // 파츠(머리)가 자식으로 붙어 있으면 함께 나간다 — 스켈레톤을 공유하므로 클립 하나가 둘을 움직인다.
  const meshes: THREE.Mesh[] = [mesh];
  mesh.traverse((o) => { const m = o as THREE.Mesh; if (m !== mesh && m.isMesh) meshes.push(m); });
  const removed = new Map<string, THREE.BufferAttribute | THREE.InterleavedBufferAttribute>();
  const restore: (() => void)[] = [];
  for (const m of meshes) {
    const g = m.geometry;
    for (const attr of CUSTOM_ATTRS) {
      const a = g.getAttribute(attr);
      if (a) { g.deleteAttribute(attr); restore.push(() => g.setAttribute(attr, a)); }
    }
    const mat = m.material as THREE.MeshPhysicalMaterial;
    const before = mat.onBeforeCompile;
    mat.onBeforeCompile = () => {};
    restore.push(() => { mat.onBeforeCompile = before; });
  }
  const geometry = mesh.geometry;
  const src = mesh.material as THREE.MeshPhysicalMaterial;
  const patched = src.onBeforeCompile;

  try {
    const exporter = new GLTFExporter();
    const result = await exporter.parseAsync(mesh, {
      binary: true,
      onlyVisible: true,
      truncateDrawRange: false,
      maxTextureSize: 2048,
      animations: mesh.animations ?? [],
    });
    if (!(result instanceof ArrayBuffer)) {
      throw new Error('binary 옵션을 켰는데 ArrayBuffer가 아닙니다.');
    }
    void name;
    return new Blob([result], { type: 'model/gltf-binary' });
  } finally {
    void geometry; void src; void patched; void removed;
    for (const r of restore) r();
  }
}

/**
 * 장면 전체(맵·지형)를 glb 하나로. 메시마다 커스텀 어트리뷰트와 셰이더 패치를 잠시 떼어 낸다.
 * 텍스처는 익스포터가 같은 이미지를 한 번만 넣으므로 타일 100개가 텍스처 100장이 되진 않는다.
 */
export async function exportSceneGLB(root: THREE.Object3D): Promise<Blob> {
  const restore: (() => void)[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const g = m.geometry;
    for (const attr of CUSTOM_ATTRS) {
      const a = g.getAttribute(attr);
      if (a) { g.deleteAttribute(attr); restore.push(() => g.setAttribute(attr, a)); }
    }
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    for (const mat of mats) {
      const patched = mat.onBeforeCompile;
      mat.onBeforeCompile = () => {};
      restore.push(() => { mat.onBeforeCompile = patched; });
    }
  });
  try {
    const exporter = new GLTFExporter();
    const result = await exporter.parseAsync(root, { binary: true, onlyVisible: true, truncateDrawRange: false, maxTextureSize: 2048 });
    if (!(result instanceof ArrayBuffer)) throw new Error('binary 옵션을 켰는데 ArrayBuffer가 아닙니다.');
    return new Blob([result], { type: 'model/gltf-binary' });
  } finally {
    for (const r of restore) r();
  }
}

/** 의존성 없는 OBJ. 텍스처는 따로 저장해야 한다 — 급할 때 형상만 보려고 쓴다. */
export function exportOBJ(mesh: THREE.Mesh): Blob {
  const geometry = mesh.geometry;
  const pos = geometry.getAttribute('position');
  const uv = geometry.getAttribute('uv');
  const normal = geometry.getAttribute('normal');
  const index = geometry.getIndex();
  const lines: string[] = ['# Texel Forge', 'o TexelForge'];

  for (let i = 0; i < pos.count; i++) {
    lines.push(`v ${pos.getX(i).toFixed(5)} ${pos.getY(i).toFixed(5)} ${pos.getZ(i).toFixed(5)}`);
  }
  if (uv) for (let i = 0; i < uv.count; i++) {
    lines.push(`vt ${uv.getX(i).toFixed(5)} ${uv.getY(i).toFixed(5)}`);
  }
  if (normal) for (let i = 0; i < normal.count; i++) {
    lines.push(`vn ${normal.getX(i).toFixed(5)} ${normal.getY(i).toFixed(5)} ${normal.getZ(i).toFixed(5)}`);
  }

  const face = (a: number, b: number, c: number) => {
    const f = (i: number) => `${i + 1}/${uv ? i + 1 : ''}/${normal ? i + 1 : ''}`;
    lines.push(`f ${f(a)} ${f(b)} ${f(c)}`);
  };

  if (index) {
    for (let i = 0; i < index.count; i += 3) {
      face(index.getX(i), index.getX(i + 1), index.getX(i + 2));
    }
  } else {
    for (let i = 0; i < pos.count; i += 3) face(i, i + 1, i + 2);
  }

  return new Blob([lines.join('\n')], { type: 'text/plain' });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** glb 헤더의 JSON 청크를 읽어 무엇이 들어갔는지 요약한다. 내보내기 검증용. */
export async function describeGLB(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const view = new DataView(buf);
  if (view.getUint32(0, true) !== 0x46546c67) return 'glb 아님';
  const jsonLen = view.getUint32(12, true);
  const json = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 20, jsonLen))) as {
    meshes?: unknown[]; skins?: { joints: number[] }[]; animations?: { name: string }[]; images?: unknown[];
  };
  const bones = json.skins?.[0]?.joints.length ?? 0;
  const clips = json.animations?.map((a) => a.name).join('·') ?? '';
  return `${(blob.size / 1024 / 1024).toFixed(2)}MB · 메시 ${json.meshes?.length ?? 0} · 뼈 ${bones} · 텍스처 ${json.images?.length ?? 0} · 클립 ${json.animations?.length ?? 0}${clips ? ` (${clips})` : ''}`;
}
